import React, { useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { useLanguage } from '@/contexts/LanguageContext';
import { promoDays, normalizePromoCode, daysPhrase, isUnlimited } from '@/lib/promoCodes';
import { storePendingCode } from '@/lib/pendingPromo';

// The code box for someone not signed in yet: on the landing, on /login and on
// the paywall. A valid code is kept for the sign-in that follows
// (sessionStorage 'ttt_event_code'; PendingCodeRedeemer redeems it once a
// session exists), then the box says what the code gives and one tap moves on.
//
// tone="brand": white on the landing's orange block. tone="light": on a white card.
const goTo = (path) => window.location.assign(path);

const STYLES = `
  .kk-code { margin:44px auto 0; max-width:420px; font-family:'Nunito',system-ui,-apple-system,'Segoe UI',sans-serif; }
  .kk-code[data-tone="light"] { margin:0; max-width:none; }
  .kk-code-title { font-size:15px; font-weight:800; color:rgba(255,255,255,.92); margin:0 0 12px; }
  .kk-code[data-tone="light"] .kk-code-title { color:#2B1A12; }
  .kk-code-row { display:flex; gap:8px; }
  .kk-code-row input {
    flex:1; min-width:0; height:46px; padding:0 14px; border-radius:14px; font:inherit; font-size:15px;
    border:2px solid transparent; background:#fff; color:#2B1A12; outline:none;
  }
  .kk-code[data-tone="light"] .kk-code-row input { border-color:#F2E3D9; }
  .kk-code-row input::placeholder { color:#B3A096; }
  .kk-code-row input[data-bad="1"] { border-color:#8E2A0C; background:#FFECE4; }
  .kk-code-row button {
    height:46px; min-width:44px; padding:0 20px; border:0; border-radius:14px; font:inherit; font-size:15px; font-weight:900;
    background:#E2551D; color:#fff; cursor:pointer; white-space:nowrap;
  }
  .kk-code small { display:block; margin-top:10px; font-size:12.5px; color:rgba(255,255,255,.72); }
  .kk-code[data-tone="light"] small { color:#6B554A; }
  .kk-code-ok {
    background:#fff; border-radius:22px; padding:18px 18px 16px; text-align:left;
    box-shadow:0 12px 30px rgba(43,26,18,.08);
  }
  .kk-code[data-tone="light"] .kk-code-ok { background:#FFF4EC; box-shadow:none; }
  .kk-code-ok p { margin:0; color:#2B1A12; font-size:16px; font-weight:800; line-height:1.4; }
  .kk-code-go {
    margin-top:12px; width:100%; min-height:48px; border:0; border-radius:14px; cursor:pointer;
    background:#F2632C; color:#fff; font:inherit; font-size:16px; font-weight:800;
    transition:transform .16s cubic-bezier(.22,1,.36,1), background .16s cubic-bezier(.22,1,.36,1);
  }
  .kk-code-go:hover { background:#E2551D; }
  .kk-code-go:active { transform:scale(.97); }
  @media (prefers-reduced-motion: reduce) { .kk-code-go { transition:none; } .kk-code-go:active { transform:none; } }
  body.reduce-motion .kk-code-go { transition:none; }
  body.reduce-motion .kk-code-go:active { transform:none; }
`;

const EventCode = ({ onGo = goTo, tone = 'brand', id, autoFocus = false }) => {
  const { t, language } = useLanguage();
  const [code, setCode] = useState('');
  const [bad, setBad] = useState(false);
  const [accepted, setAccepted] = useState(null); // days, once a code is valid
  const goRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (accepted !== null) goRef.current?.focus();
  }, [accepted]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const submit = (e) => {
    e.preventDefault();
    const upper = normalizePromoCode(code);
    const days = promoDays(upper);
    if (days !== null) {
      // Stores it and tells the redeemer, which redeems it at once if a
      // session already exists.
      storePendingCode(upper);
      try { posthog?.capture('promo_code_entered', { code: upper }); } catch {}
      setAccepted(days);
      return;
    }
    try { posthog?.capture('promo_code_invalid', { code: upper }); } catch {}
    setBad(true);
  };

  if (accepted !== null) {
    const text = isUnlimited(accepted)
      ? t('promoGrant.acceptedUnlimited')
      : t('promoGrant.accepted').replace('{days}', daysPhrase(accepted, t, language));
    return (
      <div className="kk-code" data-tone={tone} id={id}>
        <style>{STYLES}</style>
        <div className="kk-code-ok">
          <p role="status">{text}</p>
          <button ref={goRef} type="button" className="kk-code-go" onClick={() => onGo('/login')}>
            {t('promoGrant.continue')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kk-code" data-tone={tone} id={id}>
      <style>{STYLES}</style>
      <p className="kk-code-title">{t('landing.eventCodeTitle')}</p>
      <form className="kk-code-row" onSubmit={submit}>
        <input
          ref={inputRef}
          type="text"
          value={code}
          maxLength={20}
          autoCapitalize="characters"
          onChange={(e) => { setCode(e.target.value); setBad(false); }}
          placeholder={t('landing.eventCodePlaceholder')}
          aria-label={t('landing.eventCodePlaceholder')}
          data-bad={bad ? '1' : '0'}
        />
        <button type="submit">{t('landing.eventCodeCta')}</button>
      </form>
      <small>{t('landing.eventCodeNote')}</small>
    </div>
  );
};

export default EventCode;
